import Button from '@mui/material/Button';
import MUICard from '@mui/material/Card';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import QuestionLibraryBuilder from '~/common/components/QuestionLibrary/QuestionLibraryBuilder';

import Box from '../../Box';
import { useConditionsEvaluator } from '../utils/useConditionsEvaluator';
import ProcessedText from './utilityComponents/ProcessedText';

const StyledCard = styled(MUICard)(({ theme }) => ({
  borderRadius: 16,
  padding: theme.spacing(6),
}));

type CardProps = React.ComponentProps<typeof MUICard> & {
  component?: any;
};

export default function Card({ component, ...props }: Readonly<CardProps>) {
  const [isExpanded, setIsExpanded] = useState(true);
  // Subscribes the card to form changes so condition-driven visibility updates on the first change.
  const { control } = useFormContext();
  useWatch({ control });
  const {
    name,
    children,
    variant,
    title,
    subTitle,
    buttonLabel,
    count,
    placeholders,
    conditions,
    marginBottom,
    sx: componentSx,
  } = component;
  const { isVisible } = useConditionsEvaluator(conditions);
  if (!isVisible) {
    return null;
  }
  if (variant === 'accordion') {
    return (
      <StyledCard
        data-testId={name}
        variant="elevation"
        {...props}
        sx={{
          mb: marginBottom || 0,
          ...componentSx,
        }}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'nowrap',
            ...(isExpanded && {
              borderBottom: '1px solid',
              borderColor: (theme) => theme.palette.gray[200],
              paddingBottom: 3,
            }),
          }}
        >
          <Stack sx={{ flex: '1' }}>
            <Typography variant="h1" gutterBottom>
              {title}
            </Typography>
            {subTitle && (
              <Typography variant="subtitle1" gutterBottom>
                {subTitle}
              </Typography>
            )}
          </Stack>
          <Button
            variant="contained"
            onClick={() => setIsExpanded((prev) => !prev)}
            sx={{
              borderRadius: '.5rem',
              textTransform: 'none',
              whiteSpace: 'nowrap',
            }}
            startIcon={
              count ? (
                <Box
                  sx={{
                    borderRadius: '50%',
                    width: 18,
                    height: 18,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.5rem',
                    color: (theme) => theme.palette.primary.main,
                    backgroundColor: (theme) => theme.palette.common.white,
                  }}
                >
                  <ProcessedText
                    text={count}
                    name={name}
                    placeholders={placeholders}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      whiteSpace: 'nowrap',
                    }}
                  />
                </Box>
              ) : null
            }
          >
            {isExpanded ? 'Hide' : 'Show'} {buttonLabel || 'Section'}
          </Button>
        </Stack>
        <Collapse in={isExpanded} timeout="auto">
          <QuestionLibraryBuilder questions={children} />
        </Collapse>
      </StyledCard>
    );
  }
  return (
    <StyledCard
      data-testId={name}
      variant="outlined"
      {...props}
      sx={{
        mb: marginBottom || 0,
        ...componentSx,
      }}
    >
      <QuestionLibraryBuilder questions={children} />
    </StyledCard>
  );
}
